import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { accountService } from "../../../Api/Services/accountService";
import { CustomerImportResult } from "../../../Models/User";
import { FaArrowLeft, FaCheckCircle, FaDownload, FaExclamationTriangle, FaFileCsv, FaInfoCircle } from "react-icons/fa";
import { toast } from "react-toastify";
import SmartNavbar from "../../../Components/SmartNavbar/SmartNavbar";
import Button from "../../../Components/Button/Button";

const AdminCustomerImportPage = () => {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState<CustomerImportResult | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error("Selecione um arquivo CSV para importar.");
      return;
    }

    setIsUploading(true);
    setResult(null);
    try {
      const data = await accountService.importCustomers(selectedFile);
      setResult(data);
      toast.success(`Importação concluída. ${data.created} usuário(s) criado(s).`);
    } catch (error: any) {
      const message = error?.response?.data?.message ?? "Não foi possível importar os clientes.";
      toast.error(message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadTemplate = () => {
    const header = "CPF;CNPJ;E-mail;Telefone celular";
    const sampleRows = [
      "12345678901;;cliente@exemplo.com;51999999999",
      ";12345678000199;empresa@exemplo.com;51999999999"
    ];
    const blob = new Blob([`${header}\n${sampleRows.join("\n")}`], {
      type: "text/csv;charset=utf-8;"
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "modelo-importacao-clientes.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadResult = () => {
    if (!result) {
      return;
    }

    const header = [
      "Linha",
      "CPF",
      "CNPJ",
      "Email",
      "EmailGerado",
      "Telefone",
      "Senha",
      "Status",
      "Erro"
    ].join(";");

    const lines = result.rows.map((row) =>
      [
        row.rowNumber,
        row.cpf ?? "",
        row.cnpj ?? "",
        row.email ?? "",
        row.generatedEmail ?? "",
        row.phoneNumber ?? "",
        row.password ?? "",
        row.success ? "Sucesso" : "Falhou",
        row.error ?? ""
      ].join(";")
    );

    const blob = new Blob([`${header}\n${lines.join("\n")}`], {
      type: "text/csv;charset=utf-8;"
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "resultado-importacao-clientes.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const summary = useMemo(() => {
    if (!result) {
      return null;
    }
    const succeeded = result.rows.filter((r) => r.success).length;
    const failed = result.rows.length - succeeded;
    return { succeeded, failed };
  }, [result]);

  return (
    <div>
      <SmartNavbar />
      <div className="p-4 sm:p-14 max-w-full space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 text-primary-600 text-2xl font-bold">
            <FaFileCsv />
            <span>{"IMPORTAR CLIENTES"}</span>
          </div>
          <Button
            onClick={() => navigate("/admin/clientes")}
            className="w-full sm:w-auto flex justify-center items-center gap-2 px-4 py-2"
          >
            <FaArrowLeft /> {"Voltar para lista de clientes"}
          </Button>
        </div>

        <p className="text-sm text-gray-700">
          {"Importe um arquivo CSV para cadastrar clientes em massa."}
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="p-4 bg-primary-50 border border-primary-100 rounded">
              <div className="flex items-start gap-3 text-sm text-gray-700">
                <FaInfoCircle className="text-primary-600 mt-0.5 shrink-0" />
                <div className="space-y-2">
                  <p className="font-semibold">{"Formato esperado:"}</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>{"Arquivo CSV com colunas: CPF, CNPJ, E-mail, Telefone celular (não sensível a maiúsculas)."}</li>
                    <li>{"Informe apenas um documento por linha (CPF ou CNPJ)."}</li>
                    <li>{"Quando o e-mail não for informado, criaremos um e-mail temporário e ele aparecerá no resultado."}</li>
                    <li>{"Telefone é opcional; se estiver ausente ou inválido, usaremos um número padrão."}</li>
                    <li>{"O acesso (e-mail e senha) é enviado pelo WhatsApp apenas quando o celular foi informado no arquivo."}</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="border rounded">
              <div className="bg-primary-120 border-b px-4 py-2 text-sm font-semibold">{"Exemplo de preenchimento"}</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-primary-120">
                      <th className="border px-3 py-2 text-left">CPF</th>
                      <th className="border px-3 py-2 text-left">CNPJ</th>
                      <th className="border px-3 py-2 text-left">E-mail</th>
                      <th className="border px-3 py-2 text-left">{"Telefone celular"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-white">
                      <td className="border px-3 py-2">12345678901</td>
                      <td className="border px-3 py-2"></td>
                      <td className="border px-3 py-2">cliente@exemplo.com</td>
                      <td className="border px-3 py-2">51999999999</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="border px-3 py-2"></td>
                      <td className="border px-3 py-2">12345678000199</td>
                      <td className="border px-3 py-2">empresa@exemplo.com</td>
                      <td className="border px-3 py-2">51999999999</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="border rounded p-4 space-y-3">
                <div>
                  <label className="block text-sm font-semibold mb-2">{"Arquivo CSV"}</label>
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
                    className="w-full border rounded px-3 py-2 text-sm"
                    disabled={isUploading}
                  />
                  <p className="text-xs text-gray-500 mt-1">{"Tamanho máximo recomendado: 2MB."}</p>
                </div>

                <div className="flex flex-col gap-2">
                  <Button isLoading={isUploading}
                    type="submit"
                    className="py-2 flex items-center justify-center gap-2"
                    disabled={isUploading}
                  >
                    {"IMPORTAR CLIENTES"}
                  </Button>
                  <button
                    type="button"
                    onClick={handleDownloadTemplate}
                    className="flex items-center justify-center gap-2 text-primary-700 hover:text-primary-800 text-sm"
                  >
                    <FaDownload /> {"Baixar modelo CSV"}
                  </button>
                </div>

                {isUploading && (
                  <div className="flex items-center gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-100 rounded px-3 py-2">
                    <FaInfoCircle />
                    <span>{"Processando arquivo e criando usuários..."}</span>
                  </div>
                )}
              </div>
            </form>

            {result && (
              <div className="border rounded p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <FaCheckCircle className="text-green-600" />
                  <span>{"Resultado da importação"}</span>
                </div>
                <p className="text-sm text-gray-700">
                  {"Total de linhas lidas"}: {result.totalRows || result.rows.length}
                </p>
                <p className="text-sm text-gray-700">{"Criados"}: {result.created}</p>
                <p className="text-sm text-gray-700">{"Falhas"}: {result.failed}</p>
                {summary && summary.failed > 0 && (
                  <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-2">
                    <FaExclamationTriangle className="mt-0.5" />
                    <span>{"Alguns registros falharam. Veja detalhes abaixo ou baixe o CSV de resultado."}</span>
                  </div>
                )}
                <button
                  onClick={handleDownloadResult}
                  className="flex items-center gap-2 text-primary-700 hover:text-primary-800 text-sm"
                >
                  <FaDownload /> {"Baixar resultado"}
                </button>
              </div>
            )}
          </div>
        </div>

        {result && (
          <div className="border rounded">
            <div className="bg-primary-120 border-b px-4 py-2 text-sm font-semibold flex justify-between items-center">
              <span>{"Detalhes"}</span>
              <span className="text-xs text-gray-500">
                {result.rows.length} {"linhas processadas"}
              </span>
            </div>
            <div className="overflow-x-auto max-h-[60vh]">
              <table className="w-full text-sm">
                <thead className="bg-primary-120">
                  <tr>
                    <th className="border px-3 py-2 text-left">Linha</th>
                    <th className="border px-3 py-2 text-left">CPF</th>
                    <th className="border px-3 py-2 text-left">CNPJ</th>
                    <th className="border px-3 py-2 text-left">E-mail</th>
                    <th className="border px-3 py-2 text-left">{"E-mail gerado"}</th>
                    <th className="border px-3 py-2 text-left">Telefone</th>
                    <th className="border px-3 py-2 text-left">{"Senha"}</th>
                    <th className="border px-3 py-2 text-left">{"Status"}</th>
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((row) => (
                    <tr key={row.rowNumber} className="bg-white">
                      <td className="border px-3 py-2">{row.rowNumber}</td>
                      <td className="border px-3 py-2">{row.cpf ?? "-"}</td>
                      <td className="border px-3 py-2">{row.cnpj ?? "-"}</td>
                      <td className="border px-3 py-2">{row.email ?? "-"}</td>
                      <td className="border px-3 py-2">{row.generatedEmail ?? "-"}</td>
                      <td className="border px-3 py-2">{row.phoneNumber ?? "-"}</td>
                      <td className="border px-3 py-2">
                        {row.success ? row.password : "-"}
                      </td>
                      <td className="border px-3 py-2">
                        {row.success ? (
                          <span className="text-green-700">{"Criado"}</span>
                        ) : (
                          <div className="text-red-700 flex flex-col">
                            <span>{"Falhou"}</span>
                            {row.error && <span className="text-xs text-red-600">{row.error}</span>}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCustomerImportPage;
